// ExplorationModal.jsx - Modal d'accès à l'espace d'exploration depuis le chat
import React, { useState, useEffect, useCallback } from 'react';
import { 
  Database, Upload, File, BookOpen, X, Search, Loader2, 
  ChevronRight, ExternalLink, CheckCircle, AlertCircle,
  FileText, FolderOpen, Tag, BarChart3, Download,
  Eye, Info, Trash2, Calendar
} from 'lucide-react';
import { makeAuthenticatedRequest } from '../components/APIService';
import { getAuth } from 'firebase/auth';
import KnowledgeBase from '../CP-KnowledgeDB/knowledgeBase';

const BackEndUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080';

// Composant pour afficher les statistiques
const StatsBadge = ({ documentsCount, conceptsCount }) => {
  return (
    <div className="flex items-center gap-2 bg-purple-50 border border-purple-200 rounded-full px-3 py-1">
      <div className="flex items-center gap-1">
        <FileText size={12} className="text-purple-600" />
        <span className="text-xs font-medium text-purple-700">{documentsCount}</span>
      </div>
      <div className="w-px h-3 bg-purple-300"></div>
      <div className="flex items-center gap-1">
        <Tag size={12} className="text-purple-600" />
        <span className="text-xs font-medium text-purple-700">{conceptsCount}</span>
      </div>
    </div>
  );
};

// Composant d'onglet
const TabButton = ({ active, onClick, icon: Icon, label, count }) => {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-3 rounded-lg transition-all duration-200 ${
        active 
          ? 'bg-purple-100 text-purple-700 border-b-2 border-purple-500 font-medium' 
          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
      }`}
    >
      <Icon size={18} />
      <span>{label}</span>
      {count !== undefined && count > 0 && (
        <span className={`px-2 py-0.5 text-xs rounded-full ${
          active ? 'bg-purple-200 text-purple-800' : 'bg-gray-200 text-gray-700'
        }`}>
          {count}
        </span>
      )}
    </button>
  );
};

// Composant de chargement
const LoadingState = ({ message = "Chargement..." }) => (
  <div className="flex flex-col items-center justify-center py-12">
    <Loader2 className="h-8 w-8 text-purple-500 animate-spin mb-4" />
    <p className="text-gray-600">{message}</p>
  </div>
);

// Composant d'erreur
const ErrorState = ({ message, onRetry }) => (
  <div className="flex flex-col items-center justify-center py-12">
    <AlertCircle className="h-10 w-10 text-red-500 mb-4" />
    <p className="text-gray-700 mb-4 text-center">{message}</p>
    {onRetry && (
      <button
        onClick={onRetry}
        className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
      >
        Réessayer
      </button>
    )}
  </div>
);


// Composant principal
const ExplorationModal = ({
  show,
  onClose,
  userId,
  competenceData,
  onDocumentUploaded,
  onConceptSelected,
  onUseConceptInChat
}) => {
  const [activeTab, setActiveTab] = useState('upload'); // 'upload', 'documents', 'concepts'
  const [error, setError] = useState(null);
  
  // Gérer les erreurs
  const handleError = useCallback((error, context = '') => {
    const errorMessage = `${context}: ${error?.message || 'Une erreur est survenue'}`;
    console.error(errorMessage, error);
    setError(errorMessage);
    setTimeout(() => setError(null), 5000);
  }, []);

  // Gérer les activités de KnowledgeBase
  const handleActivity = useCallback((activity) => {
    console.log('Activité KnowledgeBase:', activity);
    
    switch (activity.type) {
      case 'document_uploaded':
        if (onDocumentUploaded) {
          onDocumentUploaded(activity.document);
        }
        // Basculer vers l'onglet documents après upload
        setActiveTab('documents');
        break;
        
      case 'concept_explored':
        if (onConceptSelected) {
          onConceptSelected(activity.concept);
        }
        break;
        
      case 'document_deleted':
        // Notification de suppression
        setError('✅ Document supprimé avec succès');
        setTimeout(() => setError(null), 3000);
        break;
        
      default:
        console.log('Activité non gérée:', activity.type);
    }
  }, [onDocumentUploaded, onConceptSelected]);

  // Rendu conditionnel si la modal n'est pas visible
  if (!show) return null;
  
  // Déterminer le mode d'affichage pour KnowledgeBase
  const getInitialViewMode = () => {
    switch (activeTab) {
      case 'upload': return 'upload';
      case 'documents': return 'documents';
      case 'concepts': return 'concepts';
      default: return 'documents';
    }
  };

  return (
    <>
      {/* Modal principale */}
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[85vh] overflow-hidden flex flex-col">
        
        {/* En-tête */}
        <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-200 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-50 to-purple-100 border-2 border-purple-200 flex items-center justify-center">
                <Database size={20} className="text-purple-600" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-800">Espace d'exploration</h3>
                <p className="text-sm text-gray-600">
                  Gérer votre base de connaissances pour enrichir le chat
                </p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-200"
            >
              <X size={20} className="text-gray-500" />
            </button>
          </div>
        </div>
        
        {/* Onglets */}
        <div className="sticky top-[73px] bg-white border-b border-gray-200 px-6 z-10">
          <div className="flex gap-1">
            <TabButton
              active={activeTab === 'upload'}
              onClick={() => setActiveTab('upload')}
              icon={Upload}
              label="Télécharger"
            />
            <TabButton
              active={activeTab === 'documents'}
              onClick={() => setActiveTab('documents')}
              icon={File}
              label="Documents"
            />
            <TabButton
              active={activeTab === 'concepts'}
              onClick={() => setActiveTab('concepts')}
              icon={BookOpen}
              label="Concepts"
            />
          </div>
        </div>
        
        {/* Contenu principal */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Affichage d'erreur globale */}
          {error && (
            <div className="mb-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 mr-3 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-red-700 text-sm">{error}</p>
                  </div>
                  <button
                    onClick={() => setError(null)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* KnowledgeBase intégré */}
          <KnowledgeBase
            userId={userId}
            competence={competenceData}
            initialViewMode={getInitialViewMode()}
            embedded={true}
            showHeader={false}
            onActivity={handleActivity}
          />
        </div>
        
        {/* Pied de page */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-500">
              {activeTab === 'upload' && 'Ajoutez des documents pour enrichir votre base'}
              {activeTab === 'documents' && 'Explorez vos documents'}
              {activeTab === 'concepts' && 'Découvrez les concepts identifiés'}
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </>
  );
};

export default ExplorationModal;
